/**
 * EmailComposeModal - Modal for composing and sending emails with attachments
 *
 * Used when user clicks "Email" on selected files (photos, documents, etc.) in the gallery.
 * Provides templates for different use cases (customer notifications, internal sharing, etc.)
 * Dynamically adapts to the file mix (photos only, documents only, or mixed)
 */

import { useState, useEffect, useMemo } from 'react'
import { X, Mail, Send, Loader2, AlertCircle, CheckCircle, Image, FileText, ChevronDown, Paperclip, Minimize2, Maximize2 } from 'lucide-react'

// Helper to determine file type from filename
function getFileType(filename) {
  if (!filename) return 'other'
  const ext = filename.toLowerCase().split('.').pop()
  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'tiff', 'tif']
  const docExts = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'csv']
  if (imageExts.includes(ext)) return 'image'
  if (docExts.includes(ext)) return 'document'
  return 'other'
}

// Analyze selected files to determine content type
function analyzeFilesMix(files) {
  const counts = { image: 0, document: 0, other: 0 }
  files.forEach(f => {
    const type = getFileType(f.filename)
    counts[type]++
  })

  const hasImages = counts.image > 0
  const hasDocs = counts.document > 0 || counts.other > 0

  // Determine primary content type
  if (hasImages && !hasDocs) return 'photos'
  if (hasDocs && !hasImages) return 'documents'
  return 'mixed' // has both
}

// Get appropriate label based on file mix
function getFilesLabel(count, contentType) {
  if (contentType === 'photos') return `${count} photo${count !== 1 ? 's' : ''}`
  if (contentType === 'documents') return `${count} document${count !== 1 ? 's' : ''}`
  return `${count} file${count !== 1 ? 's' : ''}`
}

// Get modal title based on content type
function getModalTitle(contentType) {
  if (contentType === 'photos') return 'Email Photos'
  if (contentType === 'documents') return 'Email Documents'
  return 'Email Files'
}

// Get icon based on content type
function getContentIcon(contentType) {
  if (contentType === 'photos') return Image
  if (contentType === 'documents') return FileText
  return Paperclip
}

// Email templates for different use cases
// Templates adapt to the content type (photos, documents, or mixed)
const EMAIL_TEMPLATES = [
  {
    id: 'files_for_review',
    name: 'Files for Review',
    icon: '📎',
    description: 'Send files for customer/team review',
    subject: (orderNum) => `Files for Review - Order ${orderNum}`,
    message: (orderNum, filesLabel, senderName) =>
      `Please find attached ${filesLabel} for Order #${orderNum} for your review.\n\n` +
      `Let me know if you have any questions or need additional information.\n\n` +
      `Thanks,\n${senderName}`,
  },
  {
    id: 'order_complete',
    name: 'Order Complete (Customer)',
    icon: '📦',
    description: 'Let customer know their order is finished',
    subject: (orderNum, contentType) => `Your Order ${orderNum} is Complete - ${contentType === 'photos' ? 'Photos' : 'Files'} Attached`,
    message: (orderNum, filesLabel, senderName) =>
      `Great news! Your order #${orderNum} has been completed and is ready for shipment.\n\n` +
      `We've attached ${filesLabel} of your finished parts for your review. ` +
      `Please let us know if you have any questions or need any adjustments before we ship.\n\n` +
      `Thank you for choosing Amtrend!\n\n` +
      `Best regards,\n${senderName}`,
  },
  {
    id: 'quality_approval',
    name: 'Quality Approval Request',
    icon: '✅',
    description: 'Request customer approval before shipping',
    subject: (orderNum) => `Quality Review Request - Order ${orderNum}`,
    message: (orderNum, filesLabel, senderName) =>
      `We've completed production on your order #${orderNum} and would like your approval before shipment.\n\n` +
      `Please review the attached ${filesLabel}. If everything looks good, ` +
      `simply reply to confirm and we'll ship immediately. If you'd like any changes, please let us know.\n\n` +
      `We appreciate your business!\n\n` +
      `Thanks,\n${senderName}`,
  },
  {
    id: 'progress_update',
    name: 'Progress Update (Customer)',
    icon: '🔄',
    description: 'Share in-progress files with customer',
    subject: (orderNum) => `Progress Update - Order ${orderNum}`,
    message: (orderNum, filesLabel, senderName) =>
      `Here's an update on your order #${orderNum}!\n\n` +
      `We've attached ${filesLabel} showing the current progress of your parts. ` +
      `Production is on track and we'll keep you updated as we move forward.\n\n` +
      `Please reach out if you have any questions.\n\n` +
      `Thanks,\n${senderName}`,
  },
  {
    id: 'spec_documents',
    name: 'Spec/Drawing Documents',
    icon: '📐',
    description: 'Send specifications or shop drawings',
    subject: (orderNum) => `Specifications & Drawings - Order ${orderNum}`,
    message: (orderNum, filesLabel, senderName) =>
      `Please find attached ${filesLabel} for Order #${orderNum}.\n\n` +
      `These include the specifications and/or shop drawings for your review. ` +
      `Please let us know if you need any clarifications or have questions.\n\n` +
      `Thanks,\n${senderName}`,
  },
  {
    id: 'internal_rfq',
    name: 'RFQ Reference (Internal)',
    icon: '📋',
    description: 'Share reference files for a new RFQ',
    subject: (orderNum, contentType) => `Reference ${contentType === 'photos' ? 'Photos' : 'Files'} from Order ${orderNum} - For RFQ Review`,
    message: (orderNum, filesLabel, senderName) =>
      `Here are ${filesLabel} from Order ${orderNum}.\n\n` +
      `These show similar work we've done that could be relevant for the new RFQ. ` +
      `Let me know if you need more details on materials, processes, or lead times for this type of work.\n\n` +
      `- ${senderName}`,
  },
  {
    id: 'internal_capability',
    name: 'Capability Showcase (Internal/Marketing)',
    icon: '🌟',
    description: 'Highlight our capabilities for sales/marketing',
    subject: (orderNum) => `Great Work to Showcase - Order ${orderNum}`,
    message: (orderNum, filesLabel, senderName) =>
      `Check out this great work from Order ${orderNum}!\n\n` +
      `These ${filesLabel} showcase our capabilities and would be excellent for ` +
      `marketing materials or customer presentations. The quality and craftsmanship really stand out.\n\n` +
      `Let me know if you'd like more details on this project.\n\n` +
      `- ${senderName}`,
  },
  {
    id: 'custom',
    name: 'Custom Message',
    icon: '✏️',
    description: 'Write your own message',
    subject: (orderNum, contentType) => `${contentType === 'photos' ? 'Photos' : 'Files'} for Order ${orderNum}`,
    message: (orderNum, filesLabel, senderName) => `\n\nThanks,\n${senderName}`,
  },
]

export default function EmailComposeModal({
  isOpen,
  onClose,
  orderNum,
  selectedPhotos = [],
  onSuccess
}) {
  const [selectedTemplate, setSelectedTemplate] = useState(EMAIL_TEMPLATES[0])
  const [showTemplates, setShowTemplates] = useState(false)
  const [to, setTo] = useState('')
  const [cc, setCc] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [senderName, setSenderName] = useState('')
  // Docked rather than dismissed. Composing an email often needs something you
  // have to go and look up — an order number, a part code — and a full-screen
  // overlay forces you to choose between finishing the draft and finding the
  // thing it needs. Minimizing keeps the draft alive while you go get it.
  const [isMinimized, setIsMinimized] = useState(false)

  // Analyze file types to determine content type
  const contentType = useMemo(() => analyzeFilesMix(selectedPhotos), [selectedPhotos])
  const filesLabel = useMemo(() => getFilesLabel(selectedPhotos.length, contentType), [selectedPhotos.length, contentType])
  const modalTitle = useMemo(() => getModalTitle(contentType), [contentType])
  const ContentIcon = useMemo(() => getContentIcon(contentType), [contentType])

  // Fetch sender info when modal opens
  useEffect(() => {
    if (isOpen) {
      const fetchSenderInfo = async () => {
        try {
          const token = localStorage.getItem('amtrend_token')
          const response = await fetch('/api/auth/m365/sender-info', {
            headers: { 'Authorization': `Bearer ${token}` }
          })
          if (response.ok) {
            const data = await response.json()
            setSenderName(data.name || 'Your Name')
          }
        } catch (err) {
          console.error('Failed to fetch sender info:', err)
          setSenderName('Your Name')
        }
      }
      fetchSenderInfo()
    }
  }, [isOpen])

  // Initialize subject and message when template changes or sender name is loaded
  useEffect(() => {
    if (isOpen && selectedTemplate && senderName) {
      // Pass contentType to subject if the template function accepts it
      setSubject(selectedTemplate.subject(orderNum, contentType))
      setMessage(selectedTemplate.message(orderNum, filesLabel, senderName))
    }
  }, [selectedTemplate, isOpen, orderNum, filesLabel, senderName, contentType])

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedTemplate(EMAIL_TEMPLATES[0])
      setIsMinimized(false)
      setTo('')
      setCc('')
      setError(null)
      setSuccess(false)
      setSending(false)
    }
  }, [isOpen])

  if (!isOpen) return null

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template)
    setShowTemplates(false)
  }

  const handleSend = async () => {
    // Validate
    if (!to.trim()) {
      setError('Please enter at least one recipient')
      return
    }

    // Parse email addresses (comma or semicolon separated)
    const toList = to.split(/[,;]/).map(e => e.trim()).filter(Boolean)
    const ccList = cc ? cc.split(/[,;]/).map(e => e.trim()).filter(Boolean) : []

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    for (const email of [...toList, ...ccList]) {
      if (!emailRegex.test(email)) {
        setError(`Invalid email address: ${email}`)
        return
      }
    }

    setSending(true)
    setError(null)

    try {
      const token = localStorage.getItem('amtrend_token')
      const response = await fetch('/api/auth/m365/send-order-photos', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          to: toList,
          cc: ccList.length > 0 ? ccList : null,
          order_num: orderNum,
          subject: subject || undefined,
          message: message || undefined,
          // Ensure xfile_ref_num values are integers (may come as strings from API)
          photo_xfile_refs: selectedPhotos.map(p => parseInt(p.xfile_ref_num, 10)),
        })
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.detail || `Failed to send email (${response.status})`)
      }

      const result = await response.json()
      setSuccess(true)

      // Notify parent and close after a brief moment
      setTimeout(() => {
        if (onSuccess) onSuccess(result)
        onClose()
      }, 1500)

    } catch (err) {
      console.error('Send email failed:', err)
      setError(err.message || 'Failed to send email')
    } finally {
      setSending(false)
    }
  }

  // Minimized → a docked bar, bottom-right. The component stays mounted, so
  // every field the user has typed is still there when they restore it.
  if (isMinimized) {
    return (
      <div className="fixed bottom-0 right-6 z-50 w-72 max-w-[calc(100vw-3rem)]">
        <button
          type="button"
          onClick={() => setIsMinimized(false)}
          className="w-full px-4 py-3 bg-gradient-to-r from-amtrend-blue to-blue-700 text-white rounded-t-xl shadow-2xl flex items-center gap-3 text-left hover:brightness-110"
          title="Restore the email you were writing"
        >
          <Mail size={16} className="shrink-0" />
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-semibold truncate">{modalTitle}</span>
            <span className="block text-xs text-white/80 truncate">
              {to.trim() ? `To ${to}` : 'No recipients yet'}
            </span>
          </span>
          <Maximize2 size={14} className="shrink-0 text-white/80" />
        </button>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-surface-dark-raised rounded-xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-amtrend-blue to-blue-700 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3 text-white">
            <Mail size={20} />
            <h2 className="text-lg font-semibold">{modalTitle}</h2>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsMinimized(true)}
              aria-label="Minimize"
              title="Minimize — keeps what you have typed"
              className="p-1 hover:bg-white/20 rounded text-white/80 hover:text-white"
            >
              <Minimize2 size={18} />
            </button>
            <button
              onClick={onClose}
              aria-label="Close"
              className="p-1 hover:bg-white/20 rounded text-white/80 hover:text-white"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Success State */}
          {success && (
            <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-lg">
              <CheckCircle size={24} />
              <div>
                <p className="font-medium">Email sent successfully!</p>
                <p className="text-sm">{filesLabel} sent to {to}</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-lg">
              <AlertCircle size={20} />
              <p className="text-sm">{error}</p>
            </div>
          )}

          {!success && (
            <>
              {/* Template Selector */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email Template
                </label>
                <button
                  onClick={() => setShowTemplates(!showTemplates)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-surface-dark-overlay border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-surface-dark transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{selectedTemplate.icon}</span>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">{selectedTemplate.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{selectedTemplate.description}</p>
                    </div>
                  </div>
                  <ChevronDown size={20} className={`text-gray-400 transition-transform ${showTemplates ? 'rotate-180' : ''}`} />
                </button>

                {/* Template Dropdown */}
                {showTemplates && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-surface-dark-raised border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 max-h-64 overflow-y-auto">
                    {EMAIL_TEMPLATES.map((template) => (
                      <button
                        key={template.id}
                        onClick={() => handleTemplateSelect(template)}
                        className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-surface-dark-overlay text-left border-b border-gray-100 dark:border-gray-800 last:border-0 ${
                          selectedTemplate.id === template.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                        }`}
                      >
                        <span className="text-xl">{template.icon}</span>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">{template.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{template.description}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* File Preview */}
              <div className="bg-gray-50 dark:bg-surface-dark-overlay rounded-lg p-4">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-2">
                  <ContentIcon size={16} />
                  <span className="font-medium">{filesLabel} from Order {orderNum}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedPhotos.slice(0, 6).map((photo, idx) => (
                    <div
                      key={photo.xfile_ref_num || idx}
                      className="text-xs bg-white dark:bg-surface-dark-raised px-2 py-1 rounded border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 truncate max-w-[150px]"
                      title={photo.filename}
                    >
                      {photo.filename}
                    </div>
                  ))}
                  {selectedPhotos.length > 6 && (
                    <div className="text-xs bg-white dark:bg-surface-dark-raised px-2 py-1 rounded border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300">
                      +{selectedPhotos.length - 6} more
                    </div>
                  )}
                </div>
              </div>

              {/* To Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  To <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  placeholder="recipient@example.com"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-surface-dark text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amtrend-blue focus:border-transparent"
                  disabled={sending}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Separate multiple emails with commas</p>
              </div>

              {/* CC Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  CC <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                  placeholder="cc@example.com"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-surface-dark text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amtrend-blue focus:border-transparent"
                  disabled={sending}
                />
              </div>

              {/* Subject Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-surface-dark text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amtrend-blue focus:border-transparent"
                  disabled={sending}
                />
              </div>

              {/* Message Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Message
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Add a personal message..."
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amtrend-blue focus:border-transparent resize-none"
                  disabled={sending}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  A table with Line, Part#, and Customer P/N will be added below your message automatically.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {!success && (
          <div className="px-6 py-4 bg-gray-50 dark:bg-surface-dark-overlay flex justify-end gap-3 border-t dark:border-gray-700 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
              disabled={sending}
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={sending || !to.trim()}
              className="flex items-center gap-2 px-5 py-2 bg-amtrend-blue text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {sending ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={16} />
                  Send Email
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
